import { motion } from 'motion/react';
import type { ShapeDrawPreview } from '@/components/canvas/elements/shape/controllers/shapeDrawGeometry';
import { getVectorPath } from '@/components/canvas/elements/shape/vector/vectorPaths';

const PREVIEW_FILL = '#e5e5e5';
const PREVIEW_STROKE = '#999';
const PREVIEW_LINE_STROKE = '#000000';
const PREVIEW_OPACITY = 0.85;

function trianglePoints(w: number, h: number): string {
  return `${w / 2},0 0,${h} ${w},${h}`;
}

function BoxShapePreview({
  preview,
}: {
  preview: Extract<ShapeDrawPreview, { type: 'box' }>;
}) {
  const { kind, rect } = preview;
  const { x, y, width, height } = rect;

  if (kind === 'circle') {
    return (
      <motion.div
        className="pointer-events-none absolute"
        style={{
          left: x,
          top: y,
          width,
          height,
          borderRadius: '50%',
          backgroundColor: PREVIEW_FILL,
          border: `1px solid ${PREVIEW_STROKE}`,
          opacity: PREVIEW_OPACITY,
          zIndex: 1002,
        }}
      />
    );
  }

  if (kind === 'rect') {
    return (
      <svg
        className="pointer-events-none absolute overflow-visible"
        width={width}
        height={height}
        style={{ left: x, top: y, zIndex: 1002, opacity: PREVIEW_OPACITY }}
      >
        <rect
          width={width}
          height={height}
          fill={PREVIEW_FILL}
          stroke={PREVIEW_STROKE}
          strokeWidth={1}
        />
      </svg>
    );
  }

  if (kind === 'triangle') {
    return (
      <svg
        className="pointer-events-none absolute overflow-visible"
        width={width}
        height={height}
        style={{ left: x, top: y, zIndex: 1002, opacity: PREVIEW_OPACITY }}
      >
        <polygon
          points={trianglePoints(width, height)}
          fill={PREVIEW_FILL}
          stroke={PREVIEW_STROKE}
          strokeWidth={1}
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  const pathD = getVectorPath(
    kind === 'heart' ? 'heart' : kind === 'star' ? 'star' : 'polygon',
    kind === 'polygon'
      ? { polygonSides: 6 }
      : kind === 'star'
        ? { starPoints: 5, starInnerRatio: 0.45 }
        : {},
  );

  return (
    <svg
      className="pointer-events-none absolute overflow-visible"
      width={width}
      height={height}
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ left: x, top: y, zIndex: 1002, opacity: PREVIEW_OPACITY }}
    >
      <path
        d={pathD}
        fill={PREVIEW_FILL}
        stroke={PREVIEW_STROKE}
        strokeWidth={1}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ShapeDrawPreviewLayer({ preview }: { preview: ShapeDrawPreview | null }) {
  if (!preview) return null;

  if (preview.type === 'line') {
    const { x1, y1, x2, y2 } = preview.segment;
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const w = Math.max(1, Math.abs(x2 - x1));
    const h = Math.max(1, Math.abs(y2 - y1));
    return (
      <svg
        className="pointer-events-none absolute overflow-visible"
        style={{ left, top, width: w, height: h, zIndex: 1002, opacity: PREVIEW_OPACITY }}
      >
        <line
          x1={x1 - left}
          y1={y1 - top}
          x2={x2 - left}
          y2={y2 - top}
          stroke={PREVIEW_LINE_STROKE}
          strokeWidth={2}
        />
      </svg>
    );
  }

  return <BoxShapePreview preview={preview} />;
}
